import { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { formatPrice } from '../../../utils/format';

export default function VentesTab() {
  const [ventesTissu, setVentesTissu] = useState<any[]>([]);
  const [commandes, setCommandes] = useState<any[]>([]);
  const [globalStats, setGlobalStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (!token) return;
      try {
        const [vtArr, cmdArr, stats] = await Promise.all([
          api.getTodayVentes(token),
          api.getCommandes(token),
          api.getGlobalStats(token)
        ]);
        setVentesTissu(Array.isArray(vtArr) ? vtArr : []);
        setCommandes(Array.isArray(cmdArr) ? cmdArr : []);
        setGlobalStats(stats);
      } catch (err) {
        console.error("Erreur chargement ventes:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const userRole = (() => {
    try {
      return JSON.parse(localStorage.getItem('atelier_bassoum_user') || '{}').role;
    } catch {
      return null;
    }
  })();

  // Aujourd'hui
  const totalTissuToday = ventesTissu.reduce((s, v) => s + Number(v.total), 0);
  const todayStr = new Date().toISOString().split('T')[0];
  const acomptesAujourdhui = commandes.filter(c => c.date_commande?.startsWith(todayStr));
  const acomptesToday = acomptesAujourdhui.reduce((s, c) => s + Number(c.acompte || 0), 0);
  const totalToday = totalTissuToday + acomptesToday;

  const handleCancelVente = async (id: number) => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler cette vente ? Le tissu sera remis en stock.")) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      const res = await api.cancelVente(token, id);
      if (res.status === 'success') {
        setVentesTissu(prev => prev.filter(v => v.id !== id));
        // Rafraîchir les stats globales
        const stats = await api.getGlobalStats(token);
        setGlobalStats(stats);
      }
    } catch (err) {
      console.error("Erreur annulation vente:", err);
      alert("Erreur lors de l'annulation.");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foret-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Résumé financier global & du jour */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-foret-600 text-white rounded-2xl p-5 shadow-xl shadow-foret-100 flex flex-col justify-between">
          <div>
            <p className="text-foret-100 text-[10px] font-bold uppercase tracking-widest mb-1">Encaissement (Total)</p>
            <h2 className="text-2xl font-black">{formatPrice(globalStats?.total_encaisse || 0)}</h2>
          </div>
          <div className="mt-4 flex items-center justify-between text-[10px] font-bold text-foret-200 border-t border-foret-500 pt-3">
             <span>AUJOURD&apos;HUI :</span>
             <span className="text-white bg-white/10 px-2 py-0.5 rounded">{formatPrice(totalToday)}</span>
          </div>
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:border-red-100 transition-colors">
          <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1">Impayés (Reste à recouvrer)</p>
          <h2 className="text-2xl font-black text-red-600">{formatPrice(globalStats?.total_dette || 0)}</h2>
          <div className="mt-3 flex items-center gap-2 text-[10px] font-black py-1 px-2 bg-red-50 text-red-500 rounded-lg w-fit">
            <i className="ri-error-warning-line" />
            <span>{globalStats?.total_impayes_count || 0} DOSSIERS EN ATTENTE</span>
          </div>
        </div>
        
        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
          <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1">Ventes Tissu (Jour)</p>
          <h2 className="text-2xl font-black text-gray-900">{formatPrice(totalTissuToday)}</h2>
          <p className="text-[10px] text-gray-400 font-bold mt-2 uppercase">{ventesTissu.length} transaction(s)</p>
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
          <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1">Commandes (Total)</p>
          <h2 className="text-2xl font-black text-gray-900">{globalStats?.commandes_count || 0}</h2>
          <div className="flex items-center gap-2 mt-2">
             <span className="w-2 h-2 rounded-full bg-blue-500" />
             <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">Volume total d&apos;activité</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dernières ventes de tissu */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-playfair font-bold text-gray-900">Ventes tissus du jour</h3>
            <i className="ri-shopping-bag-3-line text-gray-300" />
          </div>
          <div className="divide-y divide-gray-50 max-h-[400px] overflow-y-auto">
            {ventesTissu.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">Aucune vente de tissu aujourd&apos;hui</div>
            ) : ventesTissu.map(v => (
              <div key={v.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-foret-600">
                    <i className="ri-scissors-cut-line text-lg" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{v.tissu_nom}</p>
                    <p className="text-xs text-gray-500">{v.quantite} {v.tissu_unite || 'm'} · {v.client_nom || 'Client anonyme'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm font-black text-gray-900">{formatPrice(v.total, false)}</p>
                    <p className="text-[10px] text-gray-400 uppercase font-bold">{v.mode_paiement}</p>
                  </div>
                  {(userRole === 'Admin' || userRole === 'Gérant') && (
                    <button 
                      onClick={() => handleCancelVente(v.id)}
                      className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      title="Annuler cette vente"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Derniers acomptes (commandes) */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-playfair font-bold text-gray-900">Nouveaux acomptes</h3>
            <i className="ri-hand-coin-line text-gray-300" />
          </div>
          <div className="divide-y divide-gray-50 max-h-[400px] overflow-y-auto">
            {acomptesAujourdhui.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">Aucun acompte encaissé aujourd&apos;hui</div>
            ) : acomptesAujourdhui.map(c => (
              <div key={c.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-terre-600">
                    <i className="ri-folder-add-line text-lg" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">CMD #{c.code}</p>
                    <p className="text-xs text-gray-500">{c.client_nom}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-black text-foret-700">{formatPrice(c.acompte || 0, false)}</p>
                  <p className="text-[10px] text-gray-400 uppercase font-bold">Acompte</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
